/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import { denormalize } from 'normalizr';
import {
  createSelector,
  createSelectorCreator,
  defaultMemoize
} from 'reselect';

import { selectBoostWords } from '@ibm-watson/shared/modules/actions/store/boosts/selectors';
import { selectDraftExtensions } from '@ibm-watson/shared/modules/channels/store/channels/extensions-selectors';
import { isFeatureEnabled } from '@ibm-watson/shared/modules/ia/store/features/selectors';
import * as operations from '@ibm-watson/shared/modules/ia/store/operations';
import { getActionSkillId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { selectSkillLanguage } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import { FeatureFlag } from '@ibm-watson/shared/ts/feature-flags';
import {
  Action,
  DataType,
  InputValidationHandler,
  DATA_TYPE,
  Step,
  StepQuestion,
  SystemVariable,
  Variable,
  ResolverType
} from '../../../../ts/action';
import { Language, LaunchModeResponseMode } from '../../../../ts/skill';
import type { Entity, RootState } from '../../../../ts/state';
import { ActionType } from '../../components/lists/constants';
import {
  NO_MATCHES_LABEL,
  buildOptionsList,
  computeEntityFromVariable,
  computeTypeFromVariable,
  DATA_TYPE_RESPONSE_TYPES,
  DEFAULT_ACTIONS,
  ENTITY_TYPES,
  generateActionIntentName,
  getDataTypeByEntity,
  getDataTypeBySystemVariable,
  getDefaultErrorResponseByEntityType,
  getEntityType,
  hasDynamicOptions,
  isDefaultAction,
  isTopicSwitchEnabled,
  RESPONSE_TYPES,
  stepRefersToStep,
  SYSTEM_VARIABLE_TYPES,
  textExpressionPiecesToText
} from '../../util/actionUtil';
import { TransactionOperation } from '../../util/transactionUtil';
import {
  generateComplexVariableId,
  generatePropertiesForConversationalSearchVariable,
  getResultVariableId,
  isOpenApiExtensionVariable,
  parseSubActionVariableFromId,
  VARIABLE_CATEGORIES
} from '../../util/variableUtil';
import { mapSlotDescToVarTitle, mapSlotToVarType } from '../convo-skills/util';
import {
  getEntityIdFromDataType,
  selectCustomDataTypeById,
  selectCustomDataTypes,
  selectDataTypes
} from '../data-types/selectors';
import { getById as getEntityById, getEntities } from '../entities/selectors';
import { getIntegrationsVariables } from '../variables/selectors';
import {
  getById as getIntentById,
  getIntentExamples as getIntentCounterExamples,
  getIntents
} from './../intents/selectors';
import { actionSchema, actionsSchema } from './schema';

export const NOT_FOUND_HANDLER = 'validation_not_found_handler';
export const NOT_FOUND_MAX_TRIES_HANDLER =
  'validation_not_found_max_tries_handler';

const getVarType = obj => {
  const entity = _.get(obj, 'entity');
  const freeText = _.get(obj, 'free_text');
  const dataType = _.get(obj, 'data_type');
  const dynamicOption = _.pick(obj, ['source_variable', 'option_list']);
  if (freeText) {
    return ENTITY_TYPES.free_text.id;
  }
  if (!_.isEmpty(dynamicOption)) {
    return ENTITY_TYPES.dynamic_option.id;
  }
  if (entity) {
    return entity;
  }
  if (dataType) {
    return dataType;
  }
  return null;
};

/**
 * This selector creator will create a memoized selector.
 * It will compare each dependency by the following rules:
 * 1. If the item in the dependency is an object or a list of object,
 * it will only check if the object id changes. Only when id changes,
 * it will recalculate the new value.
 * 2. If the item is anything else, it will do shallow equal check as usual.
 */
const createIdsEqualSelector = createSelectorCreator(
  defaultMemoize,
  (previousList, currentList) =>
    _.isEqualWith(previousList, currentList, (itemA, itemB) => {
      if (
        itemA &&
        itemB &&
        typeof itemA === 'object' &&
        typeof itemB === 'object'
      ) {
        // We only check if the id of the object stays the same
        // for action steps, we have a synthetic id property same as `step.step`
        // so we can use the same comparator.
        if (itemA.id && itemB.id) {
          return itemA.id === itemB.id;
        }
      }
    })
);

const getConvoSkillResultVariableTitle = (title: string) =>
  `${title} result variable`;

export const getAllActions = (state: RootState): Action[] =>
  denormalize(
    _.keys(state.models.actions),
    actionsSchema,
    _.pick(state.models, ['actions', 'steps', 'action_variables', 'slots'])
  ) || [];

export const getAllActionsWithExamples = (state: RootState): Action[] => {
  const actions = getAllActions(state);
  const intents = getIntents(state);
  const counterExamples = getIntentCounterExamples(state);
  if (!_.isEmpty(intents) || counterExamples.length > 0) {
    actions.forEach(action => {
      if (action.condition) {
        if (
          'expression' in action.condition &&
          action.condition.expression === DEFAULT_ACTIONS.ANYTHING_ELSE
        ) {
          action.examples = counterExamples;
          return action;
        }
        if ('expression' in action.condition || !action.condition) {
          action.examples = [];
          return action;
        }
      }

      let actionIntentId;
      if (action.condition) {
        if ('intent' in action.condition) {
          actionIntentId = action.condition.intent;
        } else if (
          'and' in action.condition &&
          'intent' in action.condition.and[0]
        ) {
          actionIntentId = action.condition.and[0].intent;
        }
      }
      const intentObj = getIntentById(state, actionIntentId);
      action.examples = intentObj?.examples || [];
      return action;
    });
  }
  return actions;
};

/**
 * Selects the actions that are not `anything_else` or `welcome` or `fallback`.
 */
export const selectCustomActions = (state: RootState): Action[] => {
  const sessionSkillId = getActionSkillId(state);
  const actionsSkillId = state.actions?.skillId;

  // Return an empty array if the actions are for a different skillId
  if (!actionsSkillId || sessionSkillId !== actionsSkillId) {
    return [];
  } else {
    return getAllActions(state).filter(
      ({ action }) => !isDefaultAction(action)
    );
  }
};

export const selectFirstCustomAction = (state: RootState) => {
  const customActions = selectCustomActions(state);
  if (customActions.length === 0) {
    return null;
  }

  // Set it to the first action in the list, then look for and update to the oldest action
  let firstCustomAction = customActions[0];
  customActions.forEach(action => {
    if (Date.parse(action?.created) < Date.parse(firstCustomAction?.created)) {
      firstCustomAction = action;
    }
  });
  return firstCustomAction;
};

export const getAction = (
  state: RootState,
  actionId: string
): Action | null => {
  // set default titles for old default actions.
  // Once we release the action skill or we don't have any action skill older than Aug
  // we can delete these codes.
  const modelData = _.pick(state.models, [
    'actions',
    'steps',
    'action_variables'
  ]);

  const action = denormalize(actionId, actionSchema, modelData);
  if (action && !action?.steps) {
    const steps = [];
    for (const step in modelData.steps) {
      steps.push(modelData.steps[step]);
    }
    action.steps = steps;
  }
  if (
    action &&
    action.action === 'welcome' &&
    action.title !== 'Greet customer'
  ) {
    action.title = 'Greet customer';
  } else if (action && action.action === 'anything_else') {
    action.title = NO_MATCHES_LABEL;
  } else if (
    action &&
    action.action === 'fallback' &&
    action.title !== 'Fallback'
  ) {
    action.title = 'Fallback';
  }

  return action || null;
};

export const getAllActionIds = (state: RootState): Set<string> => {
  const existingActions = state.models.actions as Record<string, Action>;
  const existingActionIds = new Set(Object.keys(existingActions));

  return existingActionIds;
};

export const getAllActionTitles = (state: RootState): Set<string> => {
  const existingActions = state.models.actions as Record<string, Action>;
  const existingActionNames = new Set(
    Object.values(existingActions).map(action => action.title)
  );

  return existingActionNames;
};

export const selectIsConvoSkillActionById = (
  state: RootState,
  actionId: string
): boolean => {
  if (!actionId) {
    return false;
  }
  const action = getAction(state, actionId);
  return action?.type === ActionType.CONVERSATIONAL_SKILL;
};

export const selectShowConvoActionId = (state: RootState): string =>
  state.actions?.showConvoActionPanel?.actionId ?? '';

export const getActionLaunchMode = (
  state: RootState
): LaunchModeResponseMode => {
  return state.actions.launchMode;
};

export const getEditorContext = (state: RootState) => {
  return state.actions.editorContext;
};

export const selectUserCreatedTopics = (state: RootState) => {
  const actionList = getAllActions(state);
  if (!_.isEmpty(actionList)) {
    //get only user created actions
    const userCreatedActions = _.filter(actionList, action => {
      return _.includes(action.id, 'action_');
    });

    //retun only title and id properties
    return _.map(userCreatedActions, action => {
      const { title, id, type } = action;
      return { title, id, type };
    });
  }
};

export const getCurrentAction = (state: RootState) => {
  const action = getAction(state, state.actions.actionId);
  // This is a fix to prevent a case that is occurring where the steps on an action are not being populated correctly.
  // This doesn't correct the root cause but prevents the page from crashing on visualization when there are no steps in the action object
  if (action && !action.steps) {
    action.steps = [];
  }
  return action;
};

export const getTriggerExamples = (state: RootState) => {
  const { examples } = state.models;
  return Object.keys(examples || {});
};

export const getEndStepTypeCount = (state: RootState) => {
  let numEndSteps = 0;
  let numReturnSteps = 0;
  let numReAskSteps = 0;
  const { steps } = state.models;
  _.forEach(steps, step => {
    const type = _.get(step, 'resolver.type');
    if (type === 'end_action') {
      numEndSteps++;
    } else if (type === 'jump_to') {
      numReturnSteps++;
    } else if (type === 'replay') {
      numReAskSteps++;
    }
  });
  return {
    numEndSteps,
    numReAskSteps,
    numReturnSteps,
    totalSteps: Object.keys(steps).length
  };
};

export const getAllSteps = (state: RootState) => {
  const { steps } = state.models;
  return steps || {};
};

/**
 * get a step by its id.
 * @param {object} state redux state
 * @param {string} stepId step id
 * @returns {object} step
 */
export const getStepById = (state: RootState, stepId: string) => {
  if (stepId) {
    return _.get(state, `models.steps[${stepId}]`, null);
  }
  return null;
};

export const getCurrentStep = (state: RootState) => {
  const stepId = state?.actions?.stepId;
  if (stepId === 'trigger-conditions') {
    return { step: 'trigger-conditions' };
  }

  return getStepById(state, stepId);
};

export const getStepIndex = (state: RootState, stepId: string) => {
  const currentAction = getCurrentAction(state);
  return Math.max(
    0,
    _.findIndex(currentAction?.steps, ({ step }) => step === stepId)
  );
};

export const getSubActions = (state: RootState) => state.models.sub_actions;
export const getSubActionList = (state: RootState) =>
  _.values(state.models.sub_actions);

export const getReferencesToStep = (state: RootState, stepId: string) => {
  const currentStep = getStepById(state, stepId);
  const currentAction = getCurrentAction(state);
  if (currentStep) {
    const references = [];
    const { steps } = state.models;
    const stepsArray = Object.values(steps);
    const resultVariable = _.get(
      currentStep,
      'resolver.invoke_action.result_variable'
    );
    stepsArray.forEach(step => {
      if (
        stepRefersToStep(
          step,
          currentStep.step,
          currentStep.variable,
          resultVariable
        )
      ) {
        // we want the step index from current action, not from models because the index doesn't change here
        const stepIndex = Math.max(
          0,
          _.findIndex(
            _.get(currentAction, 'steps', []),
            (s: Step) => s.step === step.step
          )
        );
        references.push({ title: step.variableTitle, stepIndex });
      }
    });
    return references;
  }
  return [];
};

export const getReferencesToCurrentStep = (state: RootState) => {
  const { stepId } = state.actions;
  return getReferencesToStep(state, stepId);
};

export const getAssistantVariables = (state: RootState) => {
  const { variables_system } = state.models;
  return Object.values(variables_system || {}).filter(
    variable => variable.integrationVariable === false
  );
};

export const getGlobalVariables = (state: RootState): Variable[] => {
  const { variables } = state.models;
  return Object.values(variables || {});
};

export const getSubActionVariables = createIdsEqualSelector(
  (state: RootState) => {
    const currentStep = getCurrentStep(state);
    return _.get(currentStep, 'resolver.invoke_action.action');
  },
  (state: RootState) => {
    const currentStep = getCurrentStep(state);
    return _.get(currentStep, 'resolver.invoke_action.result_variable');
  },
  getSubActionList,
  selectCustomDataTypes,
  (subActionId, resultVariable, subActions, dataTypes) => {
    const subActionParams = [];
    const subAction = subActions.find(action => action.action === subActionId);
    if (subAction) {
      const subactionSteps = subAction.slots || subAction.steps;
      // TODO: Decouple slots and steps
      if (Array.isArray(subactionSteps)) {
        subactionSteps.forEach((step, index) => {
          if (
            step.entity ||
            step.free_text ||
            step.data_type ||
            step.type ||
            hasDynamicOptions(step)
          ) {
            if (subActionParams.length === 0) {
              subActionParams.push({
                category: subAction.title,
                children: [],
                resultVariable
              });
            }

            let { entity } = step;
            if (step.data_type) {
              const possibleDataType = dataTypes.find(
                d => d.data_type === step.data_type
              );
              entity = possibleDataType?.entity;
            } else if (step.free_text) {
              entity = ENTITY_TYPES.free_text.id;
            }

            subActionParams[0].children.push({
              variable: step.variable,
              entity,
              title: step.title || mapSlotDescToVarTitle(step.name),
              id: step.variable,
              index,
              category: subAction.title || 'Untitled',
              categoryId: VARIABLE_CATEGORIES.SUBACTION,
              convoSkillVariable: !!subAction.slots,
              stepDefinedIndex: index,
              stepVariable: step.variable,
              source: resultVariable,
              data_type: step.data_type || step.type || DATA_TYPE.ANY,
              type: getVarType(step) || step.type
            });
          }
        });
        return subActionParams;
      }
    }
    return subActionParams;
  }
);

export const getOrderedSteps = createSelector(
  getCurrentAction,
  currentAction => currentAction?.steps ?? []
);

//returns an object with global, assistant and integration variables
export const getVariablesExecptForStepVariables = createIdsEqualSelector(
  getGlobalVariables,
  getAssistantVariables,
  getIntegrationsVariables,
  (globalVariables, assistantVariables, integrationVariables) => {
    const nonStepVariables = {
      [VARIABLE_CATEGORIES.GLOBAL]: [],
      [VARIABLE_CATEGORIES.ASSISTANT]: [],
      [VARIABLE_CATEGORIES.INTEGRATION]: []
    };
    for (let i = 0; i < globalVariables.length; i++) {
      nonStepVariables[VARIABLE_CATEGORIES.GLOBAL].push({
        variable: globalVariables[i].variable,
        index: i,
        title: globalVariables[i].title,
        description: globalVariables[i].description,
        category: 'Global',
        categoryId: VARIABLE_CATEGORIES.GLOBAL,
        data_type: globalVariables[i].data_type
      });
    }

    for (let i = 0; i < assistantVariables.length; i++) {
      nonStepVariables[VARIABLE_CATEGORIES.ASSISTANT].push({
        entity: computeEntityFromVariable(
          assistantVariables[i].system_variable
        ),
        type: computeTypeFromVariable(assistantVariables[i].system_variable),
        variable: assistantVariables[i].system_variable,
        index: i,
        title: assistantVariables[i].title,
        description: assistantVariables[i].description,
        category: 'Assistant',
        categoryId: VARIABLE_CATEGORIES.ASSISTANT,
        readOnly: !!assistantVariables[i].read_only
      });
    }

    for (let i = 0; i < integrationVariables.length; i++) {
      nonStepVariables[VARIABLE_CATEGORIES.INTEGRATION].push({
        variable: integrationVariables[i].system_variable,
        index: i,
        title: integrationVariables[i].title,
        description: integrationVariables[i].description,
        category: 'Integration',
        categoryId: VARIABLE_CATEGORIES.INTEGRATION,
        readOnly: !!integrationVariables[i].read_only,
        type: SYSTEM_VARIABLE_TYPES[integrationVariables[i].system_variable]
      });
    }
    return nonStepVariables;
  }
);

export const getVariablesForCurrentStep = createIdsEqualSelector(
  getOrderedSteps,
  (state: RootState) => state.actions.stepId,
  getSubActionList,
  getVariablesExecptForStepVariables,
  selectDataTypes,
  selectDraftExtensions,
  (
    orderedSteps,
    stepId,
    subActions,
    nonStepVariables,
    dataTypeMap,
    openApiExtensions
  ) => {
    const allVariables = {
      [VARIABLE_CATEGORIES.STEP]: [],
      ...nonStepVariables
    };
    if (orderedSteps && stepId) {
      for (let i = 0; i < orderedSteps.length; i++) {
        const step = orderedSteps[i];
        if (stepId === orderedSteps[i].step) {
          continue;
        }
        // This type could be sys-free-text, dynamic-option, dataTypeId, entityId, null
        const type = getVarType(step.question);
        if (type) {
          const dataTypeId = step.question?.data_type;
          const dataType = dataTypeMap[dataTypeId];
          allVariables[VARIABLE_CATEGORIES.STEP].push({
            variable: step.variable,
            index: i,
            title: step.variableTitle,
            data_type: dataTypeId || DATA_TYPE.ANY,
            description: step.responseText,
            category: 'Local',
            categoryId: VARIABLE_CATEGORIES.STEP,
            entity: dataType?.entity || type,
            type
          });
        }
        const clientActionResultVar = _.get(
          step,
          'resolver.client_actions[0].result_variable'
        );
        if (clientActionResultVar) {
          allVariables[VARIABLE_CATEGORIES.STEP].push({
            variable: clientActionResultVar,
            index: i,
            title: `${step.variableTitle} - client result`,
            data_type: DATA_TYPE.ANY,
            category: 'Local',
            categoryId: VARIABLE_CATEGORIES.STEP
          });
        }

        const searchResultVariable = _.get(
          step,
          'resolver.response.after_generation.result_variable'
        );
        const searchResultVariableMode = _.get(
          step,
          'resolver.response.after_generation.mode'
        );
        if (
          searchResultVariable &&
          (searchResultVariableMode === 'store_and_display' ||
            searchResultVariableMode === 'store_only')
        ) {
          const properties =
            generatePropertiesForConversationalSearchVariable();
          allVariables[searchResultVariable] = [];
          properties.forEach((property, idx) => {
            const identifier = generateComplexVariableId(
              searchResultVariable,
              property.path
            );

            allVariables[searchResultVariable].push({
              ...property, // critical properties are 'path', 'id', 'name', 'data_type', title
              category: 'Conversational search',
              categoryId: VARIABLE_CATEGORIES.SEARCH,
              data_type: property.data_type || DATA_TYPE.ANY,
              description: property.description || property.title,
              index: idx,
              source: searchResultVariable,
              stepDefinedIndex: i, //index of step where the search resolver was encountered.
              title: property.title,
              type: property.data_type,
              entity: property.data_type,
              variable: identifier
            });
          });
        }

        const resultVariable = _.get(step, 'resolver.callout.result_variable');
        if (resultVariable) {
          allVariables[resultVariable] = [];
          const extensionCatalogId = _.get(
            step,
            'resolver.callout.internal.catalog_item_id'
          );
          const extensionMethod = _.get(step, 'resolver.callout.method');
          const extensionCalloutPath = _.get(step, 'resolver.callout.path');
          if (extensionCatalogId) {
            const extension = openApiExtensions?.find(
              ext => ext.settings?.catalog_item_id === extensionCatalogId
            );
            if (extension) {
              // get available response properties of the open api extension.
              const properties =
                extension.settings?.endpoints?.find(
                  endpoint =>
                    endpoint.path === extensionCalloutPath &&
                    endpoint.method?.toLowerCase() ===
                      extensionMethod?.toLowerCase()
                )?.response_properties ?? [];
              properties.forEach((property, idx) => {
                if (property.enabled) {
                  const identifier = generateComplexVariableId(
                    resultVariable,
                    property.path
                  );
                  allVariables[resultVariable].push({
                    ...property, // critical properties are 'path', 'id', 'name', 'data_type'
                    category: extension.name,
                    categoryId: VARIABLE_CATEGORIES.OPEN_API_EXTENSION,
                    data_type: property.data_type || DATA_TYPE.ANY,
                    description: property.description || property.title,
                    index: idx,
                    source: resultVariable,
                    stepDefinedIndex: i,
                    title: property.title,
                    type: property.data_type,
                    variable: identifier
                  });
                }
              });
            }
          }
        }

        const subActionId = _.get(step, 'resolver.invoke_action.action');
        if (subActionId) {
          const subAction = subActions.find(a => a.action === subActionId);
          if (subAction && subAction.steps) {
            const resultVariable = _.get(
              step,
              'resolver.invoke_action.result_variable'
            );
            subAction.steps.forEach((step, index) => {
              const identifier = generateComplexVariableId(
                resultVariable,
                step.variable
              );
              const type = getVarType(step);
              if (!type) {
                return;
              }
              if (!allVariables[resultVariable]) {
                allVariables[resultVariable] = [];
              }
              allVariables[resultVariable].push({
                category: subAction.title,
                categoryId: VARIABLE_CATEGORIES.SUBACTION,
                data_type: step.data_type || DATA_TYPE.ANY,
                description: step.title,
                entity: step.entity,
                index,
                source: resultVariable,
                stepDefinedIndex: i,
                stepVariable: step.variable,
                title: step.title,
                type,
                variable: identifier
              });
            });
          }
          if (subAction && subAction.slots) {
            const resultVariable = _.get(
              step,
              'resolver.invoke_action.result_variable'
            );
            // For Conversational skill actions we are not showing all variables from the step.
            // Instead we only populate the variable field for that step with one result variable
            // and it has no step variables like custom action result variable
            allVariables[resultVariable] = [
              {
                category: subAction.title,
                categoryId: VARIABLE_CATEGORIES.SUBACTION,
                convoSkillVariable: true,
                data_type: DATA_TYPE.ANY,
                description: resultVariable,
                index: 0,
                source: resultVariable,
                stepDefinedIndex: i,
                title: getConvoSkillResultVariableTitle(subAction.title),
                type,
                variable: resultVariable
              }
            ];
          }
        }
      }
    }

    return allVariables;
  }
);

export const getVariablesMapForCurrentStep = createSelector(
  getVariablesForCurrentStep,
  availableVariables => {
    const result = {};
    Object.values(availableVariables).forEach(varList => {
      varList.forEach(v => {
        result[v.variable] = v;
      });
    });
    return result;
  }
);

export const getEntitiesUpToCurrentStep = (state: RootState) => {
  const { stepId } = state.actions;
  const orderedSteps = getOrderedSteps(state);
  const entitiesMap = {};
  if (stepId && orderedSteps) {
    for (let i = 0; i < orderedSteps.length; i++) {
      if (stepId === orderedSteps[i]) {
        break;
      }
      const step = _.get(state, `models.steps[${orderedSteps[i]}]`);
      if (step) {
        const entityId = _.get(step, 'question.entity', '').toLowerCase();
        const entity = getEntityType(entityId, getEntities(state));
        if (entity) {
          if (entitiesMap[entity]) {
            entitiesMap[entity] = entitiesMap[entity] + 1;
          } else {
            entitiesMap[entity] = 1;
          }
        }
      }
    }
    return entitiesMap;
  }
};

export const getStepsUpToCurrentStep = createIdsEqualSelector(
  getOrderedSteps,
  (state: RootState) => state.actions.stepId,
  (orderedSteps, stepId) => {
    const stepsList = [{ category: 'Steps', children: [] }];
    if (Array.isArray(orderedSteps) && stepId) {
      for (let index = 0; index < orderedSteps.length; index++) {
        const step = orderedSteps[index];
        stepsList[0].children.push({
          id: step.step,
          index,
          title: step.title || step.variableTitle,
          variable: step.variable
        });
        if (stepId === step.step) {
          break;
        }
      }
    }
    return stepsList;
  }
);

export const getCurrentStepEntity = (state: RootState) => {
  const step = getCurrentStep(state);
  const dataTypeId = _.get(step, 'question.data_type');
  let entityId = _.get(step, 'question.entity');
  if (dataTypeId) {
    entityId = getEntityIdFromDataType(state, dataTypeId);
  }
  return getEntityById(state, entityId);
};

export const getCurrentStepDataType = (state: RootState) => {
  const step = getCurrentStep(state);
  const dataTypeId = _.get(step, `question.data_type`);
  return selectCustomDataTypeById(state, dataTypeId);
};

export const getCurrentActionId = (state: RootState) => {
  return _.get(state, 'actions.actionId', null);
};

export const getStepQuestion = (state: RootState, stepId: string) => {
  const step =
    stepId === 'trigger-conditions'
      ? { step: 'trigger-conditions' }
      : getStepById(state, stepId);
  return step ? (step.question as StepQuestion) : null;
};

export const getCurrentStepQuestion = (state: RootState) => {
  const step = getCurrentStep(state);
  return step ? step.question : null;
};

export const getCurrentStepHandlers = (state: RootState) => {
  const step = getCurrentStep(state);
  return step ? step.handlers : null;
};

const deriveStepEntityType = (
  question: StepQuestion,
  entitiesMap: Record<string, Entity>,
  dataTypes: Record<string, DataType>
) => {
  if (question?.free_text) {
    return ENTITY_TYPES.free_text.id;
  }
  if (question?.hasOwnProperty('data_type')) {
    return (
      dataTypes[question.data_type]?.response_type ?? ENTITY_TYPES.option.id
    );
  }
  if (question?.hasOwnProperty('source_variable')) {
    return ENTITY_TYPES.dynamic_option.id;
  }
  if (!question?.entity) {
    return null;
  }
  return getEntityType(question.entity, entitiesMap);
};

export const getStepEntityType = createSelector(
  getStepQuestion,
  getEntities,
  selectDataTypes,
  deriveStepEntityType
);

export const getCurrentStepEntityType = createSelector(
  getCurrentStepQuestion,
  getEntities,
  selectDataTypes,
  deriveStepEntityType
);

export const getCurrentStepValidation = createSelector(
  getCurrentStep,
  getCurrentStepEntityType,
  getCurrentStepHandlers,
  getCurrentStepQuestion,
  selectSkillLanguage,
  (
    step,
    entityType,
    handlers = [],
    question = {},
    language = Language.English
  ) => {
    if (!entityType || !language) {
      return {};
    }
    const notFoundHandlerIndex = _.findIndex(handlers, {
      type: 'not_found',
      handler: NOT_FOUND_HANDLER
    });

    const notFoundHandler =
      notFoundHandlerIndex > -1 ? handlers[notFoundHandlerIndex] : {};
    const inputValidationHandler = _.find(handlers, {
      type: 'input_validation'
    }) as InputValidationHandler;

    let errorResponse;
    // Change errorResponse into a simple string array
    if (notFoundHandler?.output?.generic[0]?.values[0]?.text_expression) {
      errorResponse = notFoundHandler.output.generic[0].values.map(value =>
        textExpressionPiecesToText(value.text_expression.concat)
      );
    } else {
      errorResponse = [
        _.get(notFoundHandler, 'output.generic[0].values[0].text')
      ];
    }
    return {
      errorResponse,
      defaultErrorResponse: getDefaultErrorResponseByEntityType(
        entityType,
        language
      ),
      numberOfTurns: _.get(question, 'max_tries'),
      inputValidationHandler,
      repeatOnReprompt: step?.output?.generic?.some(
        response =>
          response.repeat_on_reprompt &&
          // repeat_on_reprompt if response type is not option, date, or response_from_data_type
          ![
            RESPONSE_TYPES.option,
            RESPONSE_TYPES.date,
            RESPONSE_TYPES.response_from_data_type
          ].includes(response.response_type)
      )
    };
  }
);

const getCurrentStepOptionResponses = (state: RootState) => {
  const step = getCurrentStep(state);
  const outputGeneric = _.get(step, 'output.generic', []);
  return _.filter(outputGeneric, {
    response_type: RESPONSE_TYPES.option
  });
};

export const getOptionsAssociatedWithEntity = (optionResponseTypes, entity) => {
  if (!Array.isArray(optionResponseTypes) && entity) {
    return [];
  }
  if (!Array.isArray(optionResponseTypes) || !entity) {
    return null;
  }

  let matchOptionsIndex = 0;
  let maxMatchCount = 0;
  for (let i = 0; i < optionResponseTypes.length; i++) {
    const optionResponseType = optionResponseTypes[i];
    let matchCount = 0;

    if (optionResponseType?.options) {
      optionResponseType.options.forEach(option => {
        let entityValue = {};
        if (entity && entity.values) {
          entityValue = entity.values.find(item => item.value === option.label);
          if (entityValue) {
            matchCount++;
            if (maxMatchCount < matchCount) {
              maxMatchCount = matchCount;
              matchOptionsIndex = i;
            }
          }
        }
      });
    }
  }
  if (maxMatchCount === 0) {
    return [];
  }
  return optionResponseTypes[matchOptionsIndex].options;
};

export const getCurrentStepOptions = createSelector(
  getCurrentStepOptionResponses,
  getCurrentStepEntity,
  getCurrentStepDataType,
  (optionResponseTypes, entity, dataType) => {
    if (dataType?.response_type === DATA_TYPE_RESPONSE_TYPES.option.id) {
      return dataType.response.options;
    }
    if (_.isEmpty(optionResponseTypes) && entity?.values) {
      return buildOptionsList(entity.values);
    } else {
      return getOptionsAssociatedWithEntity(optionResponseTypes, entity);
    }
  }
);

export const selectTriggerWordValues = (state: RootState, id: string) =>
  state?.models?.entities?.[id]?.values?.map(({ value }) => value) ?? [];

type PayloadDataType = DataType & { operation: TransactionOperation };

type PayloadToSave = {
  action: Action & {
    operation?: TransactionOperation;
  };
  dataTypes: PayloadDataType[];
  entities?: {
    operation: TransactionOperation;
    entity: any;
  }[];
  entitiesToReset;
  intent: {
    operation: TransactionOperation;
    intent: string;
    examples: string[];
  };
  condition: { and: any[] };
  values: {
    operation: TransactionOperation;
    originalValue: string;
    entity: any;
    value?: any;
  }[];
  variables: any[];
  variablesToDelete: any[];
  globalVariables: any[];
  counterExamples: any[];
  type?: string;
  generative_ai?: {
    ai_guided: {
      content?: string;
      prompt_instruction?: string;
    };
  };
};

//returns true/false if step variable is private
export const isStepVariablePrivate = (state: RootState, stepId: string) => {
  return state?.models?.action_variables?.[stepId]?.privacy?.enabled ?? false;
};

export const isStepSlotFillingExamples = (state: RootState, stepId: string) => {
  return state?.models?.steps?.[stepId]?.examples ?? [];
};

export const getValuesForEntity = (state: RootState) => {
  return _.get(
    state,
    `models.entities.${state.actions.currentVarEntity}.values`,
    []
  );
};

export const getEntitiesMap = (state: RootState) => {
  // use reselect here and pass in currentStepId and if currentStepId hasn't changed dont update
  return _.get(state, `models.entities`, {});
};

export const isAutoSaveEnabled = (state: RootState) => {
  return state.actions?.autoSave;
};

export const areActionsLoading = (state: RootState) =>
  state.actions?.loadingActions || state.actions?.listLoading;

export const isActionLoading = (state: RootState) =>
  state.actions?.loadingAction;

export const isTemplateActionCreating = (state: RootState) =>
  state.actions?.creatingTemplateAction;

export const selectLastModifiedAction = createSelector(
  [getAllActions],
  actions => _.maxBy(actions, 'updated')?.updated
);
/**
 * Select an action by the given intent id.
 * @param {Object} state redux store state
 * @param {string} intentId an intent id
 * @returns {Object} the action that has the intent in the condition.
 */
export const selectActionByIntentId = (state: RootState, intentId: string) => {
  const targetActionId = _.get(state, `actions.intentsActionsMap[${intentId}]`);
  return getAction(state, targetActionId);
};

export const selectIntentActionsMap = (state: RootState) => {
  return _.get(state, `actions.intentsActionsMap`);
};

export const getSubactionVariableType = (
  state: RootState,
  variableId: string,
  childProperty
) => {
  const orderedSteps = getOrderedSteps(state);
  const targetStep = _.find(
    orderedSteps,
    step => _.get(step, 'resolver.invoke_action.result_variable') === variableId
  );
  if (!targetStep) {
    return null;
  }
  const targetSubaction = _.get(targetStep, 'resolver.invoke_action.action');
  const subaction = getSubActions(state)[targetSubaction];
  if (!subaction) {
    return null;
  }
  const targetSubStep = _.find(
    subaction.steps,
    step => step.variable === childProperty
  );
  if (!targetSubStep) {
    return null;
  }
  return getDataTypeByEntity(targetSubStep.entity);
};

export const getVariableType = (
  state: RootState,
  variableId: string,
  providedVariable
) => {
  if (!variableId && !providedVariable) {
    return null;
  }
  const actionVariables = state.models.action_variables;
  const sessionVariables = state.models.variables;
  const systemVariables = state.models.variables_system;
  const targetVariable =
    actionVariables[variableId] ||
    sessionVariables[variableId] ||
    systemVariables[variableId];
  if (!targetVariable) {
    // we cannot know if the variable is subaction variable or extension variable from the variable id
    if (isOpenApiExtensionVariable(providedVariable)) {
      return providedVariable.data_type;
    }
    // it could be a subaction id
    const { variable, child_property } =
      parseSubActionVariableFromId(variableId) || {};
    if (variable && child_property) {
      return getSubactionVariableType(state, variable, child_property);
    }
    return null;
  }
  if (
    (targetVariable as Variable).data_type &&
    (targetVariable as Variable).data_type !== DATA_TYPE.ANY
  ) {
    return (targetVariable as Variable).data_type;
  }
  if ((targetVariable as SystemVariable).system_variable) {
    return getDataTypeBySystemVariable(
      (targetVariable as SystemVariable).system_variable
    );
  }
  const orderedSteps = getOrderedSteps(state);
  const targetStep = _.find(
    orderedSteps,
    step => step.variable === (targetVariable as Variable).variable,
    0
  );
  if (targetStep) {
    // the variable is from a step
    const entity = _.get(targetStep, 'question.entity');
    const freeText = _.get(targetStep, 'question.free_text');
    if (hasDynamicOptions(targetStep.question)) {
      return DATA_TYPE.ANY;
    }
    if (freeText) {
      return DATA_TYPE.STRING;
    }
    return getDataTypeByEntity(entity);
  }
  // the variable is a session variable
  return DATA_TYPE.ANY;
};

/**
 * Get the option customer response of the step.
 * The response can only be an option response
 * @param {Object} state the redux state
 * @param {string} stepId the target step id
 * @returns {|null} the option response of the step
 */
export const getStepOptionResponse = (state: RootState, stepId: string) => {
  const entity = getCurrentStepEntity(state);
  const step = getStepById(state, stepId);
  if (!step) {
    return null;
  }
  const generic = step.output?.generic;
  if (!Array.isArray(generic)) {
    return null;
  }
  const dataTypeId = step.question?.data_type;
  if (dataTypeId) {
    const firstDataTypeResponse = generic.find(
      ({ response_type }) =>
        response_type === RESPONSE_TYPES.response_from_data_type
    );
    const dataType = selectCustomDataTypeById(state, dataTypeId);
    if (dataType.response_type === DATA_TYPE_RESPONSE_TYPES.option.id) {
      return { ...firstDataTypeResponse, ...dataType.response };
    }
  }
  const optionResponseType = generic.find(
    ({ response_type }) => response_type === RESPONSE_TYPES.option
  );

  if (!optionResponseType) {
    if (!entity) {
      return null;
    }
    //the option response type does not exist in the output, but the step
    //is associated with an entity/data type.
    //return the list of options for the step based on the entity
    const optionsList = buildOptionsList(entity.values ?? []);
    return { options: optionsList };
  } else {
    return optionResponseType;
  }
};

export const isDuplicatedButNotModifiedStep = (
  state: RootState,
  stepId: string
) => {
  return state.actions?.duplicatedButNotModifiedStepIds?.includes(stepId);
};

export const isMostRecentDuplicatedButNotModifiedStep = (
  state: RootState,
  stepId: string
) => {
  return _.last(state.actions?.duplicatedButNotModifiedStepIds) === stepId;
};

export const isTopicSwitchEnabledOnActionLevel = createSelector(
  getCurrentAction,
  currentAction => Boolean(isTopicSwitchEnabled(currentAction))
);

export const selectGlobalSettingsRedirect = (state: RootState) => {
  return _.get(state, `actions.redirectGlobalSettingsTo`);
};

export const selectWelcomeNextActionId = (state: RootState) => {
  const { actions } = state.models;
  return actions?.welcome?.next_action ?? '';
};

/**
 * Check if return variable is private or not.
 * The return variable from an extension or a subaction can either be
 * private or not.
 * @param {Object} state the redux state
 * @returns {Boolean} true or false
 */
export const isReturnVariablePrivate = (state: RootState) => {
  const currentStep = getCurrentStep(state);
  const resultVariable =
    currentStep?.resolver?.type === ResolverType.CALL_AN_EXTENSION
      ? currentStep?.resolver?.callout?.result_variable
      : currentStep?.resolver?.invoke_action?.result_variable;
  const isPrivateVariable = isStepVariablePrivate(state, resultVariable);
  return isPrivateVariable;
};

export const getAllVariables = (state: RootState) => {
  const orderedSteps = getOrderedSteps(state);
  const openApiExtensions = selectDraftExtensions(state);
  const allVariables = {};
  if (orderedSteps) {
    orderedSteps.forEach((step, index) => {
      const entity = _.get(step, 'question.entity');
      const dataTypeId = step.question?.data_type;
      const dataType = selectCustomDataTypeById(state, dataTypeId);

      allVariables[step.variable] = {
        variable: step.variable,
        index,
        title: step.variableTitle,
        data_type: dataTypeId || DATA_TYPE.ANY,
        description: step.variableTitle,
        category: 'Local',
        categoryId: VARIABLE_CATEGORIES.STEP,
        entity: dataType?.entity || entity,
        type: getVarType(step?.question)
      };
      const clientActionResultVariable = _.get(
        step,
        'resolver.client_actions[0].result_variable'
      );
      if (clientActionResultVariable) {
        allVariables[clientActionResultVariable] = {
          variable: clientActionResultVariable,
          index,
          title: `${step.variableTitle} client result`,
          data_type: DATA_TYPE.ANY,
          description: step.variableTitle,
          category: 'Local',
          categoryId: VARIABLE_CATEGORIES.STEP
        };
      }
      const searchResultVariable = _.get(
        step,
        'resolver.response.after_generation.result_variable'
      );
      const searchResultVariableMode = _.get(
        step,
        'resolver.response.after_generation.mode'
      );

      if (
        searchResultVariable &&
        (searchResultVariableMode === 'store_and_display' ||
          searchResultVariableMode === 'store_only')
      ) {
        allVariables[searchResultVariable] = {
          variable: searchResultVariable,
          index: index,
          stepDefinedIndex: index,
          title: '',
          description: '',
          category: 'Local',
          categoryId: VARIABLE_CATEGORIES.SEARCH,
          entity: null,
          type: null
        };
        const properties = generatePropertiesForConversationalSearchVariable();
        properties.forEach((property, idx) => {
          const identifier = generateComplexVariableId(
            searchResultVariable,
            property.path
          );
          allVariables[identifier] = {
            ...property, // critical properties are 'path', 'id', 'name', 'data_type'
            category: 'Conversational search',
            categoryId: VARIABLE_CATEGORIES.SEARCH,
            data_type: property.data_type || DATA_TYPE.ANY,
            description: property.description || property.title,
            index: idx,
            source: searchResultVariable,
            stepDefinedIndex: index,
            title: property.title,
            type: property.data_type,
            variable: identifier
          };
        });
      }

      const extensionResultVariable = _.get(
        step,
        'resolver.callout.result_variable'
      );
      if (extensionResultVariable) {
        const extensionCatalogId = _.get(
          step,
          'resolver.callout.internal.catalog_item_id'
        );
        if (extensionCatalogId) {
          const extensionMethod = _.get(step, 'resolver.callout.method');
          const extensionCalloutPath = _.get(step, 'resolver.callout.path');
          const extension = openApiExtensions?.find(
            ext => ext.settings?.catalog_item_id === extensionCatalogId
          );
          if (extension) {
            allVariables[extensionResultVariable] = {
              variable: extensionResultVariable,
              index: index,
              stepDefinedIndex: index,
              title: '',
              description: '',
              category: 'Local',
              categoryId: VARIABLE_CATEGORIES.OPEN_API_EXTENSION,
              entity: null,
              type: null
            };
            // get available response properties of the open api extension.
            const properties =
              extension.settings?.endpoints?.find(
                endpoint =>
                  endpoint.path === extensionCalloutPath &&
                  endpoint.method?.toLowerCase() ===
                    extensionMethod?.toLowerCase()
              )?.response_properties ?? [];
            properties.forEach((property, idx) => {
              if (property.enabled) {
                const identifier = generateComplexVariableId(
                  extensionResultVariable,
                  property.path
                );
                allVariables[identifier] = {
                  ...property, // critical properties are 'path', 'id', 'name', 'data_type'
                  category: extension.name,
                  categoryId: VARIABLE_CATEGORIES.OPEN_API_EXTENSION,
                  data_type: property.data_type || DATA_TYPE.ANY,
                  description: property.description || property.title,
                  index: idx,
                  source: extensionResultVariable,
                  stepDefinedIndex: index,
                  title: property.title,
                  type: property.data_type,
                  variable: identifier
                };
              }
            });
          }
        }
      }

      const subactionResultVariable = _.get(
        step,
        'resolver.invoke_action.result_variable'
      );

      if (subactionResultVariable) {
        const subActionId = _.get(step, 'resolver.invoke_action.action');
        if (subActionId) {
          const subActions = getSubActions(state);
          if (subActions) {
            const subAction = subActions[subActionId];
            if (subAction) {
              if (subAction.slots) {
                // Convo skill type result variable does not have step variable
                allVariables[subactionResultVariable] = {
                  variable: subactionResultVariable,
                  data_type: DATA_TYPE.ANY,
                  index: index,
                  title: getConvoSkillResultVariableTitle(subAction.title),
                  description: getConvoSkillResultVariableTitle(
                    subAction.title
                  ),
                  category: subActions[subActionId].title || 'Untitled',
                  categoryId: VARIABLE_CATEGORIES.SUBACTION,
                  entity: null,
                  type: null,
                  stepDefinedIndex: 0,
                  convoSkillVariable: !!subAction.slots
                };
              } else {
                allVariables[subactionResultVariable] = {
                  variable: subactionResultVariable,
                  data_type: DATA_TYPE.ANY,
                  index: index,
                  title: '',
                  description: '',
                  category: subActions[subActionId].title || 'Untitled',
                  categoryId: VARIABLE_CATEGORIES.SUBACTION,
                  entity: null,
                  type: null,
                  convoSkillVariable: false
                };
              }
              // we need to include slots for convo skill type subactions
              const { steps, slots } = subAction;
              let subIndex = 0;
              if (steps) {
                for (const stepId in steps) {
                  const step = steps[stepId];
                  const identifier = generateComplexVariableId(
                    subactionResultVariable,
                    step.variable
                  );
                  const possibleDataType = selectCustomDataTypeById(
                    state,
                    step.data_type
                  );
                  allVariables[identifier] = {
                    category: subActions[subActionId].title || 'Untitled',
                    categoryId: VARIABLE_CATEGORIES.SUBACTION,
                    convoSkillVariable: !!subAction.slots,
                    data_type: step.data_type || DATA_TYPE.ANY,
                    description: step.title,
                    entity: possibleDataType?.entity || step.entity,
                    index: subIndex++,
                    source: subactionResultVariable,
                    stepDefinedIndex: index,
                    stepVariable: step.variable,
                    title: step.title,
                    type: getVarType(step),
                    variable: identifier
                  };
                }
              }
              if (slots) {
                for (const slotId in slots) {
                  const slot = slots[slotId];
                  const identifier = generateComplexVariableId(
                    subactionResultVariable,
                    slot.variable
                  );
                  const possibleDataType = selectCustomDataTypeById(
                    state,
                    slot.data_type
                  );
                  allVariables[identifier] = {
                    category: subActions[subActionId].title || 'Untitled',
                    categoryId: VARIABLE_CATEGORIES.SUBACTION,
                    convoSkillVariable: !!subAction.slots,
                    data_type: mapSlotToVarType(slot.type) || DATA_TYPE.ANY,
                    title: mapSlotDescToVarTitle(slot.name),
                    entity: possibleDataType?.entity || slot.entity,
                    index: subIndex++,
                    source: subactionResultVariable,
                    stepDefinedIndex: index,
                    stepVariable: slot.variable,
                    description: mapSlotDescToVarTitle(slot.description),
                    type: slot.type,
                    variable: slot.variable
                  };
                }
              }
            }
          }
        }
      }
    });
  }
  const globalVars = getGlobalVariables(state);
  for (let i = 0; i < globalVars.length; i++) {
    allVariables[globalVars[i].variable] = {
      variable: globalVars[i].variable,
      index: i,
      title: globalVars[i].title,
      data_type: globalVars[i].data_type,
      description: globalVars[i].description,
      category: 'Global',
      categoryId: VARIABLE_CATEGORIES.GLOBAL,
      updated: globalVars[i].updated
    };

    if (globalVars[i].initial_value) {
      allVariables[globalVars[i].variable].initial_value =
        globalVars[i].initial_value;
    }
    if (globalVars[i].default_value) {
      allVariables[globalVars[i].variable].default_value =
        globalVars[i].default_value;
    }
  }
  const assistantVariables = getAssistantVariables(state);
  for (let i = 0; i < assistantVariables.length; i++) {
    allVariables[assistantVariables[i].system_variable] = {
      entity: computeEntityFromVariable(assistantVariables[i].system_variable),
      type: computeTypeFromVariable(assistantVariables[i].system_variable),
      variable: assistantVariables[i].system_variable,
      index: i,
      title: assistantVariables[i].title,
      description: assistantVariables[i].description,
      category: 'Assistant',
      categoryId: VARIABLE_CATEGORIES.ASSISTANT,
      readOnly: !!assistantVariables[i].read_only
    };
  }

  const integrationVariables = getIntegrationsVariables(state);
  for (let i = 0; i < integrationVariables.length; i++) {
    allVariables[integrationVariables[i].system_variable] = {
      entity: computeEntityFromVariable(
        integrationVariables[i].system_variable
      ),
      variable: integrationVariables[i].system_variable,
      index: i,
      title: integrationVariables[i].title,
      description: integrationVariables[i].description,
      category: 'Integration',
      categoryId: VARIABLE_CATEGORIES.INTEGRATION,
      readOnly: !!integrationVariables[i].read_only,
      type: SYSTEM_VARIABLE_TYPES[integrationVariables[i].system_variable]
    };
  }
  return allVariables;
};

export const assemblePayloadToSave = (state: RootState) => {
  const payload: Partial<PayloadToSave> = { variables: [] };
  const {
    actionId,
    boostsModified,
    dataTypes,
    entities,
    entityValueUpdatesToTrack,
    exampleEdited,
    launchMode,
    modifiedStepIds,
    removedStepVariableIds,
    isAIGuidedAction,
    aiGuided
  } = state.actions;
  const {
    counterExamples,
    examples,
    variables: globalVariables
  } = state.models;
  const steps = _.cloneDeep(state.models.steps);

  if (removedStepVariableIds && removedStepVariableIds.length > 0) {
    payload.variablesToDelete = removedStepVariableIds;
  }

  _.forEach(steps, step => {
    const title = step.variableTitle?.replace(/\s+|\n+|\r+/g, ' ').trim() || '';
    step.variableTitle = title.length > 64 ? title.substring(0, 64) : title;
    while (step.variableTitle.length > 0) {
      if (
        /[\uD800-\uDBFF]/.test(
          step.variableTitle.substring(step.variableTitle.length - 1)
        )
      ) {
        // if high surrogate is found, we need to truncate it.
        step.variableTitle = step.variableTitle.slice(0, -1);
      } else {
        break;
      }
    }
    if (_.get(step, 'question.data_type') && _.get(step, 'question.entity')) {
      delete step.question.entity;
    }
  });

  const variablesToSave = state.actions.variables;
  const action = denormalize(
    actionId,
    actionSchema,
    { ..._.pick(state.models, ['actions', 'action_variables']), steps } // need to add the sanitized steps from above here
  );
  let newAction = _.cloneDeep(action);
  delete newAction?.variables;
  if (Array.isArray(newAction?.steps)) {
    newAction.steps.forEach((step, i, list) => {
      if (i < list.length - 1) {
        step.next_step = list[i + 1].step;
      }
      if (
        step.variable &&
        modifiedStepIds.indexOf(step.step) > -1 &&
        removedStepVariableIds?.indexOf(step.variable) === -1
      ) {
        const isPrivateVariable = isStepVariablePrivate(state, step.step);
        //if the step was modified, AND the step has not been deleted, then update the variable associated with the step
        const variable = {
          variable: step.variable,
          title: step.variableTitle,
          data_type:
            step.question?.data_type ||
            getDataTypeByEntity(step.question?.entity, false),
          privacy: {
            enabled: isPrivateVariable
          }
        };
        payload.variables.push(variable);
        if (getResultVariableId(step.resolver)) {
          //the step was modified and has a result variable - update the result variable also
          payload.variables.push({
            variable: getResultVariableId(step.resolver),
            data_type: 'any',
            privacy: {
              enabled: step.resolver?.privacyEnabled ?? false
            }
          });
        }
      }
    });
  }

  if (!newAction?.title && newAction?.newAction === true) {
    //if it is a new action assign the temporary title we've
    //created based on the example text
    newAction.title = action.tempTitle;
  }
  if (newAction && !newAction?.title) {
    newAction.title = '';
  }
  newAction = {
    ...newAction,
    operation: newAction?.newAction ? operations.CREATE : operations.UPDATE
  };

  if (dataTypes) {
    payload.dataTypes = [];
    if (dataTypes.create && dataTypes.create.length > 0) {
      dataTypes.create.forEach(dataType => {
        const dataTypeToCreate = selectCustomDataTypeById(state, dataType);
        if (dataTypeToCreate) {
          payload.dataTypes.push({
            ...dataTypeToCreate,
            operation: TransactionOperation.CREATE
          });
        }
      });
    }
    if (dataTypes.update && dataTypes.update.length > 0) {
      dataTypes.update.forEach(dataType => {
        const dataTypeToUpdate = selectCustomDataTypeById(state, dataType);
        if (dataTypeToUpdate) {
          payload.dataTypes.push({
            ...dataTypeToUpdate,
            operation: TransactionOperation.UPDATE
          });
        }
      });
    }
  }

  if (entities) {
    payload.entities = [];
    payload.entitiesToReset = [];
    payload.values = []; // individual entity values that we want to track/update

    // handle entity deletions
    if (entities.delete && entities.delete.length > 0) {
      //only need the entity names for delete - no need to get their payloads from store.models
      entities.delete.forEach(entity =>
        payload.entities.push({
          entity,
          operation: TransactionOperation.DELETE
        })
      );
    }

    // handle entity update by whole entity
    // this is useful for entities tied to danger_words and
    // profane_words. for entities associated with a step,
    // we instead want to update by individual value
    if (entities.updateFully && entities.updateFully.length > 0) {
      entities.updateFully.forEach(entityId => {
        const entityToUpdate = _.get(state, `models.entities[${entityId}]`);
        payload.entities.push({
          ...entityToUpdate,
          operation: operations.UPDATE
        });
      });
    }

    // handle entity updates by individual value
    if (entities.update && entities.update.length > 0) {
      //we have some entities that need to be updated
      let update = entities.update;
      if (entities.create && entities.create.length > 0) {
        //if an entity is in the list of items to _create_ and also to _update_ then we only need to do a create
        //as the update would be a no-op
        update = _.without(entities.update, ...entities.create);
      }
      update.forEach(entity => {
        //when 'updating' an entity we want to update the values one by one instead of attempting
        //to update the whole entity as an atomic operation.
        //When an entire entity is updated, the store deletes all existing values, and replaces them with the
        //new values array provided. The side effect of this is that all value references are invalidated.
        //To avoid this situation we are updating values individually via that values API.
        //'entityValueUpdatesToTrack' tracks which values have been updated since we last saved.
        payload.entitiesToReset.push(entity);
        const entityToUpdate = _.get(state, `models.entities[${entity}]`);
        const valueChangeTracker = entityValueUpdatesToTrack[entity];
        if (valueChangeTracker) {
          if (valueChangeTracker.create) {
            valueChangeTracker.create.forEach(originalValue => {
              const valueToCreate = _.find(entityToUpdate.values, {
                originalValue
              });
              if (valueToCreate) {
                payload.values.push({
                  ...valueToCreate,
                  operation: operations.CREATE,
                  originalValue: valueToCreate.value,
                  entity //add the entity id so the transaction api can reference the parent entity
                });
              }
            });
          }
          if (valueChangeTracker.delete) {
            valueChangeTracker.delete.forEach(originalValue => {
              payload.values.push({
                originalValue,
                operation: TransactionOperation.DELETE,
                entity
              });
            });
          }
          if (valueChangeTracker.update) {
            valueChangeTracker.update.forEach(originalValue => {
              const valueToUpdate = _.find(entityToUpdate.values, {
                originalValue
              });
              if (valueToUpdate) {
                payload.values.push({
                  ...valueToUpdate,
                  operation: TransactionOperation.UPDATE,
                  entity
                });
              }
            });
          }
        }
      });
    }
    if (entities.create && entities.create.length > 0) {
      entities.create.forEach(entity => {
        const entityToCreate = _.get(state, `models.entities[${entity}]`);
        if (entityToCreate) {
          payload.entities.push({
            ...entityToCreate,
            operation: operations.CREATE
          });
        }
      });
    }
  }

  if (exampleEdited) {
    const examplesExist = examples && Object.keys(examples).length > 0;
    let intentId;
    if (newAction.condition) {
      if ('intent' in newAction.condition) {
        intentId = newAction.condition.intent;
      } else if (
        'and' in newAction.condition &&
        'intent' in newAction.condition.and[0]
      ) {
        intentId = newAction.condition.and[0].intent;
      }
    }

    if ((!newAction.condition || !intentId) && examplesExist) {
      //the action does not have a condition, and the user has added examples
      let intentName = generateActionIntentName(newAction.action);
      const intentIds = Object.keys(state.models.intents || {});
      while (intentIds.indexOf(intentName) !== -1) {
        intentName = generateActionIntentName(newAction.action);
      }
      if (newAction.condition && 'and' in newAction.condition) {
        newAction.condition.and[0] = { intent: intentName };
      } else {
        newAction.condition = { intent: intentName };
      }
      payload.intent = {
        intent: intentName,
        operation: TransactionOperation.CREATE,
        examples: Object.values(examples)
      };
    } else if (newAction.condition) {
      //could be anything else
      if (newAction.condition.expression === 'anything_else') {
        //anything else
        payload.counterExamples = Object.values(counterExamples || {});
      } else if (
        newAction.condition.intent ||
        newAction.condition?.and?.[0]?.intent
      ) {
        payload.intent = {
          intent:
            newAction.condition.intent || newAction.condition?.and?.[0]?.intent,
          operation:
            newAction.newAction === true
              ? TransactionOperation.CREATE
              : TransactionOperation.UPDATE,
          examples: Object.values(examples)
        };
        //note we will not delete the intent/condition
      }
      if (newAction.condition.and) {
        // trigger conditions
        payload.condition = newAction.condition;
      }
    }
  }
  if (variablesToSave && variablesToSave.length > 0) {
    payload.globalVariables = [];
    variablesToSave.forEach(variable => {
      payload.globalVariables.push(_.cloneDeep(globalVariables[variable]));
    });
  }
  if (
    boostsModified &&
    isFeatureEnabled(state, FeatureFlag.ACTIONS_NEW_IA_BOOST_WORDS)
  ) {
    newAction.boosts = selectBoostWords(state);
  }

  if (launchMode) {
    newAction.launch_mode = launchMode;
  }

  if (isAIGuidedAction) {
    newAction.type = 'ai_guided';
    newAction.generative_ai = {
      ai_guided: aiGuided
    };
  }

  // If users are creating a new action from a collection, add the collection reference
  if (state.collections?.currentCollectionId && newAction?.newAction) {
    newAction = {
      ...newAction,
      collection_references: [
        { collection: state.collections?.currentCollectionId }
      ]
    };
  }

  payload.action = newAction;
  return payload;
};

export const getKnowledgeContentFromCurrentAction = (state: RootState) => {
  const { aiGuided } = state.actions;
  return aiGuided.content;
};

export const getModelIdFromCurrentAction = (state: RootState) => {
  const { aiGuided } = state.actions;
  return aiGuided.model_id;
};

export const getPromptInstructionFromCurrentAction = (state: RootState) => {
  const { aiGuided } = state.actions;
  return aiGuided.prompt_instruction;
};

export const getStreamResponseValue = (state: RootState) => {
  const orderedSteps = getOrderedSteps(state);
  const targetStep = _.find(orderedSteps, step =>
    _.get(step, 'resolver.callout.stream_response_mapping')
  );

  if (!targetStep) {
    return null;
  }
  const streamResponseValue = _.get(
    targetStep,
    'resolver.callout.stream_response_mapping.partial_item[0].mapping[0].value',
    ''
  );
  return streamResponseValue;
};
